// applications/dossier hooks
// TanStack Query (useQuery/useMutation) + client-state hooks only.
// Pure API functions belong in services/. Keys come from ../_shared/keys.ts — never array literals.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { STALE_TIME } from "@core/libs/react-query";
import { i18n } from "@core/libs/i18n";
import { toLocale } from "@core/locale";
import { isApiError } from "@core/errors";
import { applicationKeys } from "@modules/applications/_shared/keys";
import { createApplication, getApplication } from "../services";
import type { Application } from "../types";

/** Query options for one dossier, shared by a route guard and the screen so they hit one entry. */
export function applicationDetailQueryOptions(
  applicationId: string,
  locale: string
) {
  return queryOptions({
    queryKey: applicationKeys.detail(applicationId, locale),
    queryFn: () => getApplication(applicationId, locale),
    /**
     * Operational, not reference. The payload carries `server_now`, the live deadline and
     * `can_edit` — a long tier would let a closed round keep looking editable, which is the one
     * thing this screen must not get wrong.
     */
    staleTime: STALE_TIME.operational,
    // A 404 here means "not yours, or not there" — a verdict, not a blip. Retrying cannot change it.
    retry: false,
  });
}

/**
 * Get-or-create the dossier for a round, from a route loader.
 *
 * A plain async function rather than a `useMutation`, because the caller is `beforeLoad` — not
 * React, so there are no hooks available, and the result decides a redirect rather than updating a
 * screen. Same shape and contract as `@core/auth`'s `tryGetProfile` and `ensurePublicRound`.
 *
 * The response is SEEDED into the detail cache under the same key the dossier screen reads, so the
 * redirect that follows renders from cache instead of immediately refetching what we were just
 * handed. That is the whole reason this returns the parsed Application rather than only its id.
 *
 * `null` on any `ApiError` — the round closed (`409 intake_closed`), the caller holds no applicant
 * surface (`403`), the id is malformed (`400`). All of them mean "no dossier, redirect somewhere
 * honest" and none of them is worth distinguishing to an applicant standing at the door. A
 * `DataContractError` is NOT swallowed: a contract break must surface loudly rather than
 * masquerading as a closed round.
 */
export async function ensureApplication(
  queryClient: QueryClient,
  intakeRoundId: string
): Promise<Application | null> {
  const locale = toLocale(i18n.resolvedLanguage ?? "fr");
  try {
    const application = await createApplication(intakeRoundId);
    queryClient.setQueryData(
      applicationKeys.detail(application.id, locale),
      application
    );
    return application;
  } catch (error) {
    if (isApiError(error)) return null;
    throw error;
  }
}

/**
 * One dossier, inside React.
 *
 * Keyed on the ACTIVE language: the payload's schema, labels and legal copy are localized
 * server-side, so switching language must refetch rather than leave the previous language on screen.
 */
export function useApplication(applicationId: string) {
  const { i18n: instance } = useTranslation();
  const locale = toLocale(instance.resolvedLanguage ?? "fr");

  const query = useQuery({
    ...applicationDetailQueryOptions(applicationId, locale),
    // The id comes from a route param the loader already resolved, so it is always present — stated
    // rather than assumed, per the explicit-`enabled` rule.
    enabled: applicationId !== "",
  });

  return { query, application: query.data ?? null };
}

export { useDossierAutosave } from "./useDossierAutosave";
export type {
  SaveStatus,
  SavableSection,
  MemberMutationResult,
  DocumentMutationResult,
} from "./useDossierAutosave";
export { useDossierReview } from "./useDossierReview";
export type { ConsentMutationResult, SubmitResult } from "./useDossierReview";
export { partitionWarnings } from "./warnings";
